import { Text, ComboboxItem, Group, Stack, Title } from "@mantine/core";
import { FlatHierarchy, IPNSProps } from "@mctypes/global.types";
import { IIssueBoardApi, IIssueViewEnum } from "@mcapps/shared";
import React, { useEffect, useMemo, useState } from "react";
import { EditSaveAbort, FailedAlert, MCLoader, McTitle } from "../../../../components";
import { McAutocomplete } from "../../../../components/McAutocomplete";
import McDropdownNew from "../../../../components/McDropdown/MCDropdownNew";
import McInputNew from "../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../components/McInput/McTextAreaNew";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { usePutProjectByProjectIdIssueboardAndBoardIdMutation } from "../../../../../openapi";
import {
  COMPLETED_ISSUES_FETCH_LIMIT_OPTIONS,
  ISSUE_ACTION_NAME_OPTIONS,
  ISSUE_BOARD_STATUSES,
  ISSUE_DASHBOARD_LS_KEY,
  ISSUE_DASHBOARD_OPTIONS,
  ISSUE_REGISTER_NAME_OPTIONS,
  ISSUE_VIEWS,
  YES_NO
} from "../issueConstants";
import { setActiveDashboardInRedux } from "../issueSlice";
import SettingsCardWrapper from "./SettingsCardWrapper";
const SettingsGroup: React.FC<{
  title: string;
  style?: React.CSSProperties;
  className?: string;
}> = ({ title, style, className, children }) => {
  return (
    <Stack
      style={{
        minWidth: 280,
        maxWidth: 400,
        flex: "1 1 320px",
        ...style,
      }}
      className={className}
    >
      <Title
        order={5}
        style={{
          fontWeight: 400,
          paddingBottom: 4,
          borderBottom: "2px solid #d0d5dc",
        }}
      >
        {title}
      </Title>
      {children}
    </Stack>
  );
};

const FIELD_MIN_HEIGHT = 72;

const IssueCommonSettings: React.FC<{ issueBoard: IIssueBoardApi }> = ({ issueBoard }) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [pns, setPns] = useState<string>(issueBoard.pns || "");
  const [status, setStatus] = useState<string>("");
  const [showConclusion, setShowConclusion] = useState<boolean>(false);
  const [defaultView, setDefaultView] = useState<IIssueViewEnum | null>(null);
  const [defaultTemplate, setDefaultTemplate] = useState<string>("");
  const [issueNameSetting, setIssueNameSetting] = useState<string>("");
  const [taskNameSetting, setTaskNameSetting] = useState<string>("");
  const [dashboardSetting, setDashboardSetting] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [bucketNameSetting, setBucketNameSetting] = useState<string>("");
  const [showCompletedTodosInKanban, setCompletedTodosInKanban] = useState<boolean>(false);
  const [disableDeleteIssue, setDisableDeleteIssue] = useState<boolean>(false);
  const [completedIssuesFetchLimitMonths, setCompletedIssuesFetchLimitMonths] = useState<number | null>(null);

  const project = useAppSelector((state) => state.adminReducer.project);
  const dispatch = useAppDispatch();
  const [updateIssueBoard, { isLoading: updateIssueBoardLoading, error: updateBoardError }] =
    usePutProjectByProjectIdIssueboardAndBoardIdMutation();

  const error = updateBoardError;
  const isLoading = updateIssueBoardLoading;
  const tableTemplates = issueBoard.tableTemplates?.filter((template) => template.archived !== true) ?? [];
  const activeTableTemplate = tableTemplates.find((template) => template._id === issueBoard.defaultTemplate);

  useEffect(() => {
    setName(issueBoard.name);
    setStatus(issueBoard.status);
    setShowConclusion(issueBoard.showConclusion);
    setDefaultView(issueBoard.defaultView);
    setDefaultTemplate(issueBoard.defaultTemplate);
    setIssueNameSetting(issueBoard.issueNameSetting);
    setTaskNameSetting(issueBoard.taskNameSetting);
    setDashboardSetting(issueBoard.dashboardSetting);
    setCategory(issueBoard.category);
    setDescription(issueBoard.description);
    setBucketNameSetting(issueBoard.bucketNameSetting);
    setCompletedTodosInKanban(issueBoard.showCompletedTodosInKanban);
    setDisableDeleteIssue(issueBoard.disableDeleteIssue);
    setCompletedIssuesFetchLimitMonths(issueBoard.completedIssuesFetchLimitMonths ?? null);
  }, [issueBoard]);

  const _save = async () => {
    await updateIssueBoard({
      projectId: project._id,
      boardId: issueBoard._id,
      body: {
        name,
        pns,
        status: status as any,
        showConclusion,
        defaultView,
        defaultTemplateId: defaultTemplate,
        issueNameSetting,
        taskNameSetting,
        dashboardSetting,
        category,
        description,
        bucketNameSetting,
        showCompletedTodosInKanban,
        disableDeleteIssue,
        completedIssuesFetchLimitMonths,
      },
    });
    setEditMode(false);
  };
  const defaultViewIsValid = ISSUE_VIEWS.map((item) => item.key).includes(defaultView);
  const pnsItems = useMemo(
    () =>
      (project?.pns as FlatHierarchy<IPNSProps>)?.map((pns) => ({
        ...pns,
        comboLabel: [pns.props.pnsId, pns.props.title]
          .map((part) => part?.trim())
          .filter((part): part is string => part != null && part.length > 0)
          .join(" - "),
      })) ?? [],
    [project?.pns],
  );
  const pnsAutocompleteData: ComboboxItem[] = useMemo(
    () =>
      (pnsItems ?? []).map((item) => ({
        value: item._id,
        label: item.comboLabel,
      })),
    [pnsItems],
  );
  const selectedPnsLabel = useMemo(
    () => pnsItems.find((item) => item.props.pnsId === pns || item.comboLabel === pns)?.comboLabel ?? pns ?? "",
    [pnsItems, pns],
  );

  return (
    <SettingsCardWrapper title="Innstillinger">
      {isLoading && <MCLoader loading={isLoading} />}
      {error && <FailedAlert />}

      {!error && (
        <Group align="flex-start" wrap="wrap">
          {/* Generelt */}
          <SettingsGroup title="Generelt">
            <Stack gap="0">
              <McTitle title="Navn" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McInputNew title="Navn" type="text" value={name} onChange={(e) => setName(e.target.value)} />
                ) : (
                  <Text mb={0}>{name}</Text>
                )}
              </McTitle>

              <McTitle title="Status" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McDropdownNew
                    currentValue={ISSUE_BOARD_STATUSES.find((item) => item.value === status)}
                    options={ISSUE_BOARD_STATUSES}
                    onChange={(_, newValue) => setStatus(newValue.value)}
                    displayKey="label"
                  />
                ) : (
                  <Text mb={0}>{ISSUE_BOARD_STATUSES.find((item) => item.value === status)?.label}</Text>
                )}
              </McTitle>

              <McTitle title="Beskrivelse" style={{ minHeight: FIELD_MIN_HEIGHT, marginBottom: ".3em" }}>
                {editMode === true ? (
                  <McTextAreaNew
                    value={description}
                    placeholder="Skriv inn beskrivelse"
                    autosize
                    spellCheck={false}
                    style={{ width: "100%" }}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                ) : (
                  <Text mb={0}>{issueBoard.description || "Ingen beskrivelse"}</Text>
                )}
              </McTitle>

              <McTitle title="Type register" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McInputNew
                    title="Type register"
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  />
                ) : (
                  <Text mb={0}>{issueBoard.category}</Text>
                )}
              </McTitle>

              <McTitle title="PNS" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McAutocomplete
                    clearable
                    data={pnsAutocompleteData}
                    value={selectedPnsLabel}
                    onClear={() => setPns("")}
                    onCommit={(value) => {
                      const selectedPns = pnsItems.find(
                        (item) => item._id === value || item.comboLabel === value || item.props.pnsId === value,
                      );
                      setPns(selectedPns?.props?.pnsId ?? "");
                    }}
                  />
                ) : (
                  <Text mb={0}>{issueBoard.pns || ""}</Text>
                )}
              </McTitle>
            </Stack>
          </SettingsGroup>

          {/* Struktur */}
          <SettingsGroup title="Struktur" style={{ maxWidth: 400 }}>
            <Stack gap="0">
              <McTitle title="Navn på hovednivå" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McDropdownNew
                    currentValue={issueNameSetting}
                    options={ISSUE_REGISTER_NAME_OPTIONS.map((item) => item.type)}
                    onChange={(_, newValue) => setIssueNameSetting(newValue)}
                  />
                ) : (
                  <Text mb={0}>{issueBoard.issueNameSetting}</Text>
                )}
              </McTitle>

              <McTitle title="Navn på undernivå" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McDropdownNew
                    currentValue={taskNameSetting}
                    options={ISSUE_ACTION_NAME_OPTIONS.map((item) => item.singular)}
                    onChange={(_, newValue) => setTaskNameSetting(newValue)}
                  />
                ) : (
                  <Text mb={0}>{issueBoard.taskNameSetting}</Text>
                )}
              </McTitle>

              <McTitle title="Navn på bucket" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McInputNew
                    title="Navn"
                    type="text"
                    value={bucketNameSetting}
                    onBlur={(e) => setBucketNameSetting(e.target.value)}
                  />
                ) : (
                  <Text mb={0}>{issueBoard.bucketNameSetting}</Text>
                )}
              </McTitle>
            </Stack>
          </SettingsGroup>

          {/* Visning */}
          <SettingsGroup title="Visning" style={{ maxWidth: 400 }}>
            <Stack gap="0">
              <McTitle title="Velg standard dashboard" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McDropdownNew
                    currentValue={dashboardSetting}
                    options={ISSUE_DASHBOARD_OPTIONS}
                    onChange={(_, newValue) => {
                      localStorage.removeItem(ISSUE_DASHBOARD_LS_KEY);
                      setDashboardSetting(newValue);
                      dispatch(setActiveDashboardInRedux(newValue));
                    }}
                  />
                ) : (
                  <Text mb={0}>{issueBoard.dashboardSetting}</Text>
                )}
              </McTitle>

              <McTitle title="Standardmal" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McDropdownNew
                    currentValue={tableTemplates.find((f) => f._id === defaultTemplate)}
                    options={tableTemplates}
                    onChange={(_, newValue) => setDefaultTemplate(newValue._id)}
                    displayKey="name"
                    onClear={defaultTemplate ? () => setDefaultTemplate("") : undefined}
                  />
                ) : (
                  <Text mb={0}>
                    {tableTemplates.find((f) => f._id === activeTableTemplate?._id)?.name ?? "Ikke satt"}
                  </Text>
                )}
              </McTitle>

              <McTitle title="Standardvisning" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McDropdownNew
                    currentValue={ISSUE_VIEWS.find((item) => item.key === defaultView)}
                    options={ISSUE_VIEWS}
                    onChange={(_, newValue) => setDefaultView(newValue.key)}
                    displayKey="label"
                  />
                ) : (
                  <Text mb={0}>
                    {defaultViewIsValid ? ISSUE_VIEWS.find((item) => item.key === defaultView)?.label : "Ikke satt"}
                  </Text>
                )}
              </McTitle>

              <McTitle title="Vis konklusjon kun hvis status fullført" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McDropdownNew
                    currentValue={YES_NO.find((x) => x.value === showConclusion) || YES_NO[1]}
                    options={YES_NO}
                    onChange={(_, newValue) => setShowConclusion(newValue.value)}
                    displayKey="label"
                  />
                ) : (
                  <Text mb={0}>{showConclusion === true ? YES_NO[0].label : YES_NO[1].label}</Text>
                )}
              </McTitle>

              <McTitle title="Vis fullførte sjekklistepunkter i kanban" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McDropdownNew
                    currentValue={YES_NO.find((x) => x.value === showCompletedTodosInKanban) || YES_NO[1]}
                    options={YES_NO}
                    onChange={(_, newValue) => setCompletedTodosInKanban(newValue.value)}
                    displayKey="label"
                  />
                ) : (
                  <Text mb={0}>{showCompletedTodosInKanban === true ? YES_NO[0].label : YES_NO[1].label}</Text>
                )}
              </McTitle>

              <McTitle title="Ta vekk muligheten til å arkivere saker" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McDropdownNew
                    currentValue={YES_NO.find((x) => x.value === disableDeleteIssue) || YES_NO[1]}
                    options={YES_NO}
                    onChange={(_, newValue) => setDisableDeleteIssue(newValue.value)}
                    displayKey="label"
                  />
                ) : (
                  <Text mb={0}>{disableDeleteIssue === true ? YES_NO[0].label : YES_NO[1].label}</Text>
                )}
              </McTitle>

              <McTitle title="Ikke hent fullførte saker eldre enn:" style={{ minHeight: FIELD_MIN_HEIGHT }}>
                {editMode === true ? (
                  <McDropdownNew
                    currentValue={
                      COMPLETED_ISSUES_FETCH_LIMIT_OPTIONS.find(
                        (option) => option.value === completedIssuesFetchLimitMonths,
                      ) ?? COMPLETED_ISSUES_FETCH_LIMIT_OPTIONS[0]
                    }
                    options={COMPLETED_ISSUES_FETCH_LIMIT_OPTIONS}
                    onChange={(_, newValue) => setCompletedIssuesFetchLimitMonths(newValue.value)}
                    displayKey="label"
                  />
                ) : (
                  <Text mb={0}>
                    {COMPLETED_ISSUES_FETCH_LIMIT_OPTIONS.find(
                      (option) => option.value === completedIssuesFetchLimitMonths,
                    )?.label ?? COMPLETED_ISSUES_FETCH_LIMIT_OPTIONS[0].label}
                  </Text>
                )}
              </McTitle>
            </Stack>
          </SettingsGroup>

          <Stack gap="md" style={{ flexBasis: "100%" }}>
            <EditSaveAbort
              disabled={!name || name.replace(/\s/g, "").length === 0}
              editable
              big
              saveCb={_save}
              editMode={editMode}
              editModeCb={setEditMode}
            />
          </Stack>
        </Group>
      )}
    </SettingsCardWrapper>
  );
};

export default IssueCommonSettings;
